import { useMemo, useState } from "react";
import { Collapse, Grid, Icon, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip } from "@mui/material";
import MDBox from "components/MDBox";
import MDSelect from "components/MDSelect";
import MDTypography from "components/MDTypography";
import { useMaterialUIController } from "context";
import Painel from "./Painel";
import { formatarNumero, rotuloCurtoMes, temaGrafico } from "./analiseUtils";
import { formatarMoedaCentavos } from "./acordosUtils";

const formatoPct = new Intl.NumberFormat("pt-BR", { style: "percent", minimumFractionDigits: 1, maximumFractionDigits: 1 });
const formatoPctTotal = new Intl.NumberFormat("pt-BR", { style: "percent", minimumFractionDigits: 2, maximumFractionDigits: 2 });
const pct = (v) => (v === null || v === undefined ? "–" : formatoPct.format(v));

// Intensidade de uma cor só (a das fichas) proporcional ao %: é magnitude, não identidade de pessoa
const fundoIntensidade = (percentual, darkMode) => {
    if (!percentual) return "transparent";
    const alfa = (darkMode ? 0.1 : 0.06) + (darkMode ? 0.6 : 0.64) * percentual;
    return darkMode ? `rgba(144, 133, 233, ${alfa.toFixed(3)})` : `rgba(74, 58, 167, ${alfa.toFixed(3)})`;
};
const textoIntensidade = (percentual, darkMode, padrao) => {
    if (!percentual || percentual < 0.6) return padrao;
    return darkMode ? "#0b0b0b" : "#ffffff";
};

/**
 * Participação de cada negociador (ou coordenador) nas fichas acionadas dos clientes escolhidos, mês a mês: cada mês
 * soma 100% das fichas desses clientes (com mais de um, um compilado). "Média" = participação no período inteiro (os
 * meses pesam pelas fichas de cada um). Ao lado, o resumo dos clientes (todas as pessoas).
 */
export default function DetalheCliente({ blocos, meses, clientes, selecionados, onSelecionados, resumo, pessoasSelecionadas = [], cores, rotuloPessoa }) {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;
    const tema = temaGrafico(darkMode);
    const [aberto, setAberto] = useState(true);

    const clientesOptions = clientes.map(c => ({ id: c, value: c, label: c }));
    const valorClientes = clientesOptions.filter(o => selecionados.includes(o.value));
    const nomeClientes = selecionados.join(", ");

    const { linhas, totaisMes, totalGeral } = useMemo(() => {
        const qtdMeses = meses.length;
        // Fichas de cada pessoa nos clientes escolhidos, somadas mês a mês (cada ficha é de um só cliente)
        const pessoas = blocos
            .map(b => {
                const doCliente = b.linhas.filter(l => selecionados.includes(l.cliente));
                const quantidades = Array.from({ length: qtdMeses }, (_, i) => doCliente.reduce((acc, l) => acc + ((l.quantidades || [])[i] || 0), 0));
                return { nome: b.nome, quantidades, quantidadeTotal: quantidades.reduce((acc, v) => acc + v, 0) };
            })
            .filter(p => p.quantidadeTotal > 0);
        const totais = Array.from({ length: qtdMeses }, (_, i) => pessoas.reduce((acc, p) => acc + (p.quantidades[i] || 0), 0));
        const geral = totais.reduce((acc, v) => acc + v, 0);
        return {
            linhas: pessoas
                .map(p => ({
                    nome: p.nome,
                    quantidades: p.quantidades,
                    percentuais: p.quantidades.map((q, i) => (totais[i] > 0 ? (q || 0) / totais[i] : null)),
                    media: geral > 0 ? p.quantidadeTotal / geral : null,
                }))
                .sort((a, b) => (b.media || 0) - (a.media || 0) || a.nome.localeCompare(b.nome)),
            totaisMes: totais,
            totalGeral: geral,
        };
    }, [blocos, meses, selecionados]);

    const celula = {
        display: "table-cell",
        py: 0.6,
        px: 1.25,
        fontSize: "0.8rem",
        color: `${tema.texto} !important`,
        borderBottom: `1px solid ${tema.borda}`,
        whiteSpace: "nowrap",
        verticalAlign: "middle",
    };
    const cabecalho = { ...celula, fontWeight: "bold", color: `${tema.textoSecundario} !important`, fontSize: "0.75rem" };
    const fundoSelecionado = darkMode ? "rgba(255, 255, 255, 0.10)" : "rgba(26, 115, 232, 0.08)";
    const fundoTotal = darkMode ? "rgba(255, 255, 255, 0.06)" : "#f0f2f5";

    const celulaPct = (valor, quantidade, totalMes, chave) => (
        <TableCell
            key={chave}
            align="center"
            sx={{
                ...celula,
                background: `${fundoIntensidade(valor, darkMode)} !important`,
                color: `${textoIntensidade(valor, darkMode, tema.texto)} !important`,
            }}
        >
            <Tooltip title={totalMes ? `${formatarNumero(quantidade || 0)} de ${formatarNumero(totalMes)} fichas` : "Sem fichas no mês"} placement="top">
                <span>{valor ? pct(valor) : totalMes ? "0,0%" : "–"}</span>
            </Tooltip>
        </TableCell>
    );

    const itensResumo = resumo ? [
        { rotulo: "Fichas Acionadas", valor: formatarNumero(resumo.fichas) },
        { rotulo: "Atendimentos", valor: formatarNumero(resumo.atendimentos) },
        { rotulo: "Negociações", valor: formatarNumero(resumo.acordos) },
        { rotulo: "Conversão", valor: pct(resumo.conversao) },
        { rotulo: "Valor Negociado", valor: formatarMoedaCentavos(resumo.valorAcordos) },
        { rotulo: "Valor Recebido", valor: formatarMoedaCentavos(resumo.recebido) },
        { rotulo: "Ticket Médio", valor: resumo.ticketMedio === null ? "–" : formatarMoedaCentavos(resumo.ticketMedio) },
    ] : [];

    return (
        <Grid container spacing={3}>
            <Grid item xs={12} lg={9}>
                <Painel>
                    <MDBox display="flex" justifyContent="space-between" alignItems="center" gap={1} flexWrap="wrap" mb={aberto ? 1.5 : 0}>
                        <MDBox display="flex" alignItems="center" gap={1} sx={{ color: "inherit" }}>
                            <IconButton size="small" onClick={() => setAberto(!aberto)} aria-label={aberto ? "Recolher" : "Expandir"}>
                                <Icon sx={{ color: tema.texto }}>{aberto ? "expand_less" : "expand_more"}</Icon>
                            </IconButton>
                            <MDTypography variant="button" fontWeight="bold" color="text">
                                Detalhamento por Cliente e {rotuloPessoa}
                            </MDTypography>
                        </MDBox>
                        <MDBox sx={{ px: 1.5, py: 0.25, borderRadius: 4, border: `1px solid ${tema.borda}` }}>
                            <MDTypography variant="caption" color="text">
                                Cada mês representa 100% das fichas acionadas para {selecionados.length > 1 ? "os clientes escolhidos" : "o cliente"}.
                            </MDTypography>
                        </MDBox>
                    </MDBox>
                    <Collapse in={aberto}>
                        <MDBox display="flex" alignItems="center" gap={1.5} mb={1.5} flexWrap="wrap">
                            <MDTypography variant="caption" fontWeight="bold" color="text">Cliente</MDTypography>
                            <MDBox sx={{ minWidth: 220, maxWidth: 560, flexGrow: 1 }}>
                                <MDSelect isMulti name="clienteDetalhe" value={valorClientes} options={clientesOptions}
                                    onChange={v => {
                                        const escolhidos = (v || []).map(o => o.value);
                                        if (escolhidos.length) onSelecionados(escolhidos);
                                    }} />
                            </MDBox>
                            <MDTypography variant="caption" color="text" sx={{ opacity: 0.8 }}>
                                Escolha um ou mais: com mais de um, os clientes ficam somados.
                            </MDTypography>
                        </MDBox>
                        {linhas.length === 0 ? (
                            <MDTypography variant="caption" color="text" sx={{ display: "block", textAlign: "center", py: 3 }}>
                                Nenhuma ficha acionada {selecionados.length > 1 ? "destes clientes" : "deste cliente"} no período.
                            </MDTypography>
                        ) : (
                            <TableContainer sx={{ boxShadow: "none", background: "transparent", borderRadius: 0 }}>
                                <Table size="small" sx={{ display: "table", minWidth: 640 }}>
                                    <TableHead sx={{ display: "table-header-group" }}>
                                        <TableRow sx={{ display: "table-row" }}>
                                            <TableCell sx={cabecalho}>{rotuloPessoa}</TableCell>
                                            {meses.map(m => <TableCell key={m} align="center" sx={cabecalho}>{rotuloCurtoMes(m)}</TableCell>)}
                                            <TableCell align="center" sx={cabecalho}>
                                                <Tooltip title="Participação no período inteiro (média dos meses ponderada pelas fichas de cada mês)" placement="top">
                                                    <span>Média</span>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody sx={{ display: "table-row-group" }}>
                                        {linhas.map(l => (
                                            <TableRow key={l.nome} sx={{ display: "table-row", background: pessoasSelecionadas.includes(l.nome) ? fundoSelecionado : "transparent" }}>
                                                <TableCell sx={{ ...celula, fontWeight: pessoasSelecionadas.includes(l.nome) ? "bold" : undefined }}>
                                                    <MDBox display="flex" alignItems="center" gap={1} sx={{ color: "inherit" }}>
                                                        <MDBox component="span" sx={{ width: 10, height: 10, minWidth: 10, borderRadius: "2px", background: cores.cor(l.nome) }} />
                                                        {l.nome}
                                                    </MDBox>
                                                </TableCell>
                                                {l.percentuais.map((v, i) => celulaPct(v, l.quantidades[i], totaisMes[i], meses[i]))}
                                                <TableCell align="center" sx={{ ...celula, fontWeight: "bold" }}>{pct(l.media)}</TableCell>
                                            </TableRow>
                                        ))}
                                        <TableRow sx={{ display: "table-row", background: fundoTotal }}>
                                            <TableCell sx={{ ...celula, fontWeight: "bold" }}>Total</TableCell>
                                            {totaisMes.map((t, i) => (
                                                <TableCell key={meses[i]} align="center" sx={{ ...celula, fontWeight: "bold" }}>
                                                    {t > 0 ? formatoPctTotal.format(1) : "–"}
                                                </TableCell>
                                            ))}
                                            <TableCell align="center" sx={{ ...celula, fontWeight: "bold" }}>
                                                {totalGeral > 0 ? formatoPctTotal.format(1) : "–"}
                                            </TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </Collapse>
                </Painel>
            </Grid>
            <Grid item xs={12} lg={3}>
                <Painel rotulo={`${selecionados.length > 1 ? "Resumo dos Clientes" : "Resumo do Cliente"} - ${nomeClientes}`}>
                    {itensResumo.map(item => (
                        <MDBox
                            key={item.rotulo}
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            py={0.6}
                            sx={{ borderBottom: `1px solid ${tema.borda}` }}
                        >
                            <MDTypography variant="caption" color="text">{item.rotulo}</MDTypography>
                            <MDTypography variant="caption" color="text" fontWeight="bold">{item.valor}</MDTypography>
                        </MDBox>
                    ))}
                    <MDTypography variant="caption" color="text" sx={{ display: "block", mt: 1, opacity: 0.8 }}>
                        Todos os {rotuloPessoa === "Coordenador" ? "coordenadores" : "negociadores"} {selecionados.length > 1 ? "nos clientes" : "no cliente"}, com os filtros de período, carteira e status.
                    </MDTypography>
                </Painel>
            </Grid>
        </Grid>
    );
}
